import { isChromeVersionUpToDate } from "@/functions/isChromeVersionUpToDate";
import {
  ExtensionResponse,
  sendMessageToExtension,
} from "@/functions/sendMessageToExtension";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { isIOS, isMobile, isSafari } from "react-device-detect";

import { IRootState } from "../store";
import { User } from "../user/userSlice";

export type ClubImportPage =
  | "import-overview"
  | "setup-extension"
  | "club-import";

export type ClubImportTab = "club-import" | "club-import-sbc";

export type ExtensionEnabledStatus = "not-checked" | "enabled" | "disabled";

// Minimum extension version for Fast Import / Send to Extension (Safari iOS vs Chrome).
const MIN_EXTENSION_VERSION_SAFARI = [2, 2, 27] as const;
const MIN_EXTENSION_VERSION_CHROME = [2, 3, 8] as const;

// Latest Chrome extension release. Below this the import notice warns.
const LATEST_CHROME_EXTENSION_VERSION = [2, 4, 30] as const;

// The version the warning asks for, so the copy follows the constant.
export const LATEST_CHROME_EXTENSION_VERSION_LABEL =
  LATEST_CHROME_EXTENSION_VERSION.join(".");

interface ClubImportState {
  browser: "desktopChrome" | "iosSafari" | "other";

  iosExtensionInstalled: boolean;
  extensionEnabledStatus: ExtensionEnabledStatus;
  hasEaAccess: boolean;
  extensionVersion: string | null;
}

// Safari on an iPhone or iPad. Not react-device-detect's isMobileSafari: it
// counts every browser on an iPad, so Chrome there passed as Safari. Chrome's
// user agent names it (CriOS) even when it asks for desktop sites.
const isIOSSafari = isIOS && isSafari;

const initialState: ClubImportState = {
  browser: window?.chrome
    ? "desktopChrome"
    : isIOSSafari
      ? "iosSafari"
      : "other",
  extensionEnabledStatus: "not-checked",
  hasEaAccess: false,
  extensionVersion: null,
  iosExtensionInstalled: localStorage.getItem("FROM_EXTENSION_APP") === "true",
};

export const checkExtensionAvailability = createAsyncThunk(
  "clubImport/checkExtensionAvailability",
  async ({ user }: { user: User }, { dispatch, getState }) => {
    const { browser } = (getState() as IRootState).clubImportReducer;
    const safariMobile = browser === "iosSafari" || isIOSSafari;
    try {
      const res = await sendMessageToExtension({ user, safariMobile });
      if (res?.msg === "confirmation") {
        dispatch(setExtensionEnabledStatus("enabled"));
        dispatch(setHasEaAccess(!!res.hasEaAccess));
        dispatch(setExtensionVersion(res.version ?? null));
      }
    } catch {
      dispatch(setExtensionEnabledStatus("disabled"));
    }
  },
);

const clubImportSlice = createSlice({
  name: "clubImport",
  initialState,
  reducers: {
    setBrowser(state, action: PayloadAction<ClubImportState["browser"]>) {
      state.browser = action.payload;
    },
    setExtensionEnabledStatus(
      state,
      action: PayloadAction<ExtensionEnabledStatus>,
    ) {
      state.extensionEnabledStatus = action.payload;
    },
    setIosExtensionInstalled(state, action: PayloadAction<boolean>) {
      state.iosExtensionInstalled = action.payload;
      localStorage.setItem(
        "FROM_EXTENSION_APP",
        action.payload ? "true" : "false",
      );
    },
    setHasEaAccess(state, action: PayloadAction<boolean>) {
      state.hasEaAccess = action.payload;
    },
    setExtensionVersion(state, action: PayloadAction<string | null>) {
      state.extensionVersion = action.payload;
    },
    syncExtensionStatusFromResponse(
      state,
      action: PayloadAction<ExtensionResponse>,
    ) {
      if (action.payload.msg === "confirmation") {
        state.extensionEnabledStatus = "enabled";
        state.hasEaAccess = !!action.payload.hasEaAccess;
        state.extensionVersion = action.payload.version ?? null;
      }
    },
  },
});

export const {
  setExtensionEnabledStatus,
  setBrowser,
  setIosExtensionInstalled,
  setHasEaAccess,
  setExtensionVersion,
  syncExtensionStatusFromResponse,
} = clubImportSlice.actions;

export const selectClubImportState = (state: IRootState) =>
  state.clubImportReducer;

/**
 * Whether the tutorial's "First part" is done: a supported browser, a logged-in
 * account, and the extension enabled — the three steps that section shows.
 */
export const selectClubImportFirstPartComplete = (state: IRootState) => {
  const { browser, iosExtensionInstalled, extensionEnabledStatus } =
    state.clubImportReducer;
  const { user } = state.userReducer;

  return (
    !!user?.uuid &&
    extensionEnabledStatus === "enabled" &&
    ((browser === "iosSafari" && iosExtensionInstalled) ||
      browser === "desktopChrome")
  );
};

export const selectExtensionCheckReady = (state: IRootState) => {
  const { browser, iosExtensionInstalled } = state.clubImportReducer;

  return (
    (browser === "iosSafari" && iosExtensionInstalled) ||
    browser === "desktopChrome"
  );
};

export const selectExtensionSupportsLatestFeatures = (state: IRootState) => {
  const { extensionVersion, browser } = state.clubImportReducer;
  if (extensionVersion === null) {
    return true;
  }
  const threshold =
    browser === "iosSafari"
      ? MIN_EXTENSION_VERSION_SAFARI
      : MIN_EXTENSION_VERSION_CHROME;
  return isChromeVersionUpToDate([...threshold], extensionVersion);
};

// The one place the Chrome extension lives. window.chrome is set on Android
// too, so the device check is part of it — and Safari, whose extension comes
// from the App Store and is switched on in iOS Settings, is never this.
export const selectIsDesktopChrome = (state: IRootState) =>
  !isMobile && state.clubImportReducer.browser === "desktopChrome";

// The version a desktop extension reported, or null when there is nothing to
// judge.
const desktopExtensionVersion = (state: IRootState) => {
  const { extensionEnabledStatus, extensionVersion } = state.clubImportReducer;

  if (!selectIsDesktopChrome(state)) return null;
  if (extensionEnabledStatus !== "enabled") return null;
  return extensionVersion || null;
};

// A desktop extension reported a version below `min`. False when there is no
// version to judge, like the warning below.
export const selectChromeExtensionBelow =
  (min: readonly number[]) => (state: IRootState) => {
    const version = desktopExtensionVersion(state);
    return !!version && !isChromeVersionUpToDate([...min], version);
  };

// Whether to warn that the extension is behind the latest release.
// False when there is no version to judge — a missing or silent extension
// isn't something to nag about.
// Kept apart from selectExtensionSupportsLatestFeatures so bumping this
// doesn't also hide Send to Extension or fire the tutorial banner.
export const selectShouldWarnExtensionOutdated = (state: IRootState) => {
  const version = desktopExtensionVersion(state);
  if (!version) return false;

  return !isChromeVersionUpToDate(
    [...LATEST_CHROME_EXTENSION_VERSION],
    version,
  );
};

export default clubImportSlice.reducer;
